import showFREDialogCallout from '../components/FREDialogCallout';
import { logUsage } from 'owa-analytics';
import { getCalendarEntry, getDefaultCalendar } from 'owa-calendar-cache';
import { getDefaultOnlineMeetingsEnabled } from 'owa-calendar-cloud-settings';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lighted, LightningId, shouldShowLightningItem } from 'owa-lightning-v2';
import loc from 'owa-localize';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { LocalCalendarEntry } from 'owa-graph-schema';
import {
    onlineMeetingFREDialogTitle,
    onlineMeetingFREDialogTeamsDescription,
    onlineMeetingFREDialogSkypeDescription,
    onlineMeetingFREDialogSkypeConsumerDescription,
    onlineMeetingFREDialogOkButton,
    onlineMeetingFREDialogCancelButton,
} from './showOnlineMeetingFREDialog.locstring.json';

/**
 * Show a FRE dialog to teach the users how to turn on online meetings by default
 * It will be shown only once after the user save an event for which they had to turn on the online meeting manually
 */
export default async function showOnlineMeetingFREDialog() {
    // Don't show the FRE dialog if:
    // - the feature is not enabled or
    // - the setting is already turned on or
    // - the user already saw the dialog
    if (
        !isFeatureEnabled('cal-mf-enableDefaultOnlineMeetingSetting') ||
        getDefaultOnlineMeetingsEnabled() ||
        !shouldShowLightningItem(LightningId.DefaultOnlineMeetingSetting)
    ) {
        return;
    }

    const onlineMeetingFREDialogDescription = getDialogDescriptionBasedOnDefaultProvider();

    if (!onlineMeetingFREDialogDescription) {
        return;
    }

    const response = await confirm(
        loc(onlineMeetingFREDialogTitle),
        onlineMeetingFREDialogDescription,
        false /* resolveImmediately */,
        {
            // Wait until dialog is dismissed to avoid focus interruption
            delayCallbackAfterAnimation: true,
            okText: loc(onlineMeetingFREDialogOkButton),
            cancelText: loc(onlineMeetingFREDialogCancelButton),
        }
    );

    let interactionType;

    if (response === DialogResponse.ok) {
        // Navigate to Events and invitations settings
        lazyMountAndShowFullOptions.importAndExecute('calendar', 'eventAndInvitations', 'event');
        interactionType = 'GoToSettingsClicked';

        // Show a callout and anchor it to the online meetings option
        showFREDialogCallout(() => document.getElementById('onlineMeetingsOption'));
    } else {
        interactionType = 'DismissClicked';
    }

    // End lightning
    lighted(LightningId.DefaultOnlineMeetingSetting);
    logUsage('OnlineMeetingFREDialog', { interactionType: interactionType });
}

function getDialogDescriptionBasedOnDefaultProvider(): string | null {
    const defaultCalendar = getDefaultCalendar();

    if (!defaultCalendar) {
        return null;
    }

    const calendarEntry: LocalCalendarEntry = getCalendarEntry(
        defaultCalendar
    ) as LocalCalendarEntry;
    const onlineMeetingProvider = calendarEntry?.DefaultOnlineMeetingProvider;

    return onlineMeetingProvider === 'TeamsForBusiness'
        ? loc(onlineMeetingFREDialogTeamsDescription)
        : onlineMeetingProvider === 'SkypeForBusiness'
        ? loc(onlineMeetingFREDialogSkypeDescription)
        : onlineMeetingProvider === 'SkypeForConsumer'
        ? loc(onlineMeetingFREDialogSkypeConsumerDescription)
        : null;
}
